import { useMemo } from 'react';

import { DeliverableType } from 'src/shared/constants/DeliverableType';
import type {
  BasicJobInfoFragment,
  Job,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import {
  JobImageViewpoints,
  JobModelStates,
  useGetScansQuery,
} from 'src/shared/graphql/generated';
import { isMobileApp } from 'src/shared/helpers/environment';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const ATTACHABLE_STATES = [JobModelStates.Complete, JobModelStates.Processing];
const ATTACHABLE_DELIVERABLES = [
  DeliverableType.COMPLETE,
  DeliverableType.ROOF_ONLY,
  DeliverableType.CAPTURE_ONLY,
];

export const useGetScans = () => {
  const { data, loading, error } = useGetScansQuery({
    variables: { archived: false },
  });

  if (loading) return { loading: true, scans: [] };

  if (error || !data) return { loading: false, scans: [] };

  return {
    loading: false,
    scans: data.jobs.results.filter(job => !job?.example) as Job[],
  };
};

export const useGetFailedScans = () => {
  const { data, loading, error } = useGetScansQuery();

  if (loading || error || !data) {
    return [];
  }

  return data.jobs.results.filter(job => job?.modelFailureReason) as Job[];
};

export const useGetFailedProjectScans = (project?: ProjectType) =>
  useMemo(
    () =>
      project?.jobs?.find(
        job => job?.state === JobModelStates.Failed || job?.modelFailureReason,
      ),
    [project],
  );

export const useAttachableScans = () => {
  const { data, loading, error } = useGetScansQuery();

  if (loading || error || !data) {
    return [];
  }

  return data.jobs.results.filter(
    job =>
      !job?.example &&
      job?.state &&
      job?.deliverable?.id &&
      ATTACHABLE_STATES.includes(job.state) &&
      ATTACHABLE_DELIVERABLES.includes(job?.deliverable?.id),
  ) as Job[];
};

export const useScanImage = (
  bestFrontImage: BasicJobInfoFragment['bestFrontImage'],
  images: BasicJobInfoFragment['images'],
  viewpoint: JobImageViewpoints = JobImageViewpoints.Exterior,
) => {
  const imagesFiltedByViewpoint = images.filter(
    image => image.viewpoint === viewpoint,
  );

  if (viewpoint !== JobImageViewpoints.Exterior) {
    return imagesFiltedByViewpoint[0]?.rotatedImageMediumUrl;
  }

  if (bestFrontImage) {
    return bestFrontImage.rotatedImageMediumUrl;
  }

  return imagesFiltedByViewpoint.find(image => image.cubeFace === 0)
    ?.rotatedImageMediumUrl;
};

export type LinkoutType = {
  buttonText: string;
  description: string;
  title: string;
  buttonHref: string;
  subtitle?: string;
  hideOnMobileApp?: boolean;
};

const exploreServicesLinkTypes = [
  'color_consultations',
  'custom_design_services',
  'get_an_estimate',
];

export const useAvailableProjectCards = (project?: ProjectType) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const LINKOUT_META: { [key: string]: LinkoutType } = {
    color_consultations: {
      title: t('projects.links.colorConsultations.title'),
      description: t('projects.links.colorConsultations.description'),
      buttonText: t('projects.links.colorConsultations.buttonText'),
      buttonHref: 'https://americascolorconsultants-hover.com',
      hideOnMobileApp: false,
    },
    connect_with_a_pro: {
      title: t('projects.links.connectPro.title'),
      description: t('projects.links.connectPro.description'),
      buttonText: t('projects.links.connectPro.buttonText'),
      buttonHref: 'https://www.buildzoom.com/hover',
      hideOnMobileApp: false,
    },
    custom_design_services: {
      title: t('projects.links.customDesignServices.title'),
      description: t('projects.links.customDesignServices.description'),
      buttonText: t('projects.links.customDesignServices.buttonText'),
      buttonHref: 'https://hover.to/homeowner-designs',
      hideOnMobileApp: true,
    },
    scan_for_measurements: {
      title: t('projects.links.scanForMeasurements.title'),
      description: t('projects.links.scanForMeasurements.description'),
      buttonText: t('projects.links.scanForMeasurements.buttonText'),
      subtitle: t('projects.links.scanForMeasurements.subtitle'),
      buttonHref: '',
      hideOnMobileApp: false,
    },
    get_an_estimate: {
      buttonText: t('projects.links.getAnEstimate.buttonText'),
      title: t('projects.links.getAnEstimate.title'),
      description: t('projects.links.getAnEstimate.description'),
      buttonHref:
        'https://app.mykukun.com/Home-Renovation-Costs?affiliate=hover',
      hideOnMobileApp: false,
    },
  };

  const links = project?.links;

  const showComingSoon = !!project?.links?.includes('scan_for_measurements');

  const showConnectPro = !!links?.includes('connect_with_a_pro');

  const showExploreServices = exploreServicesLinkTypes.some(linkType =>
    links?.includes(linkType),
  );

  return {
    showComingSoon,
    showConnectPro,
    showExploreServices,
    scanForMeasurementsConfig: LINKOUT_META.scan_for_measurements,
    connectWithAProConfig: LINKOUT_META.connect_with_a_pro,
    exploreServicesConfigs: [
      {
        name: 'custom_design_services',
        config: LINKOUT_META.custom_design_services,
      },
      { name: 'color_consultations', config: LINKOUT_META.color_consultations },
      { name: 'get_an_estimate', config: LINKOUT_META.get_an_estimate },
    ].filter(({ config }) => !(isMobileApp() && config.hideOnMobileApp)),
  };
};
